import { useCallback, useMemo, useState } from 'react';
import { useQuery } from '@apollo/client';
import uniq from 'lodash/uniq';
import {
  MISSIONS_PURCHASE_ORDER_ENRICHMENT,
  PURCHASE_ORDERS,
  PURCHASE_ORDER_DASHBOARD_KPIS,
  PURCHASE_ORDER_MISSION_COVERAGES,
} from '@bloomays-lib/adapter.api-bloomer';
import { PurchaseOrderDashboardKpis, PurchaseOrderMissionCoverage } from '@bloomays-lib/types.shared';
import {
  buildMissingRows,
  buildMissionEnrichmentMap,
  DashboardPurchaseOrder,
  MissingPurchaseOrderRow,
  MissionEnrichment,
  MissionEnrichmentResult,
} from '../helpers/purchaseOrderDashboard';

export type UsePurchaseOrderDashboardResult = {
  loading: boolean;
  error?: Error;
  kpis?: PurchaseOrderDashboardKpis;
  coverages: PurchaseOrderMissionCoverage[];
  purchaseOrders: DashboardPurchaseOrder[];
  missingRows: MissingPurchaseOrderRow[];
  missionEnrichmentByRecordId: Record<string, MissionEnrichment>;
  amByMissionRecordId: Record<string, string>;
  missingEnrichmentLoading: boolean;
  referenceDate: Date;
  lastUpdatedAt: Date;
  refetchAll: () => Promise<void>;
};

/**
 * Orchestration des données du dashboard BDC : KPIs de couverture, couvertures
 * mission, liste de tous les BDC et enrichissement des missions manquantes
 * (TJM, bloomer, AM) via une requête GraphQL dédiée.
 */
export const usePurchaseOrderDashboard = (): UsePurchaseOrderDashboardResult => {
  const [lastUpdatedAt, setLastUpdatedAt] = useState<Date>(new Date());

  const kpisQuery = useQuery<{ purchaseOrderDashboardKpis: PurchaseOrderDashboardKpis }>(PURCHASE_ORDER_DASHBOARD_KPIS);
  const coveragesQuery = useQuery<{ purchaseOrderMissionCoverages: PurchaseOrderMissionCoverage[] }>(
    PURCHASE_ORDER_MISSION_COVERAGES,
  );
  const purchaseOrdersQuery = useQuery<{ purchaseOrders: DashboardPurchaseOrder[] }>(PURCHASE_ORDERS);

  const coverages = useMemo<PurchaseOrderMissionCoverage[]>(
    () => coveragesQuery.data?.purchaseOrderMissionCoverages ?? [],
    [coveragesQuery.data],
  );
  const missingRows = useMemo<MissingPurchaseOrderRow[]>(() => buildMissingRows(coverages), [coverages]);
  const missionIds = useMemo<string[]>(
    () =>
      uniq([
        ...coverages.map((coverage) => coverage.missionRecordId),
        ...missingRows.map((row) => row.missionRecordId),
      ]),
    [coverages, missingRows],
  );

  const enrichmentQuery = useQuery<{ allMissions: { results: MissionEnrichmentResult[] } }>(
    MISSIONS_PURCHASE_ORDER_ENRICHMENT,
    { variables: { missionIds }, skip: missionIds.length === 0 },
  );

  const missionEnrichmentByRecordId = useMemo<Record<string, MissionEnrichment>>(
    () => buildMissionEnrichmentMap(enrichmentQuery.data?.allMissions?.results ?? []),
    [enrichmentQuery.data],
  );

  const amByMissionRecordId = useMemo<Record<string, string>>(() => {
    const map: Record<string, string> = {};
    Object.entries(missionEnrichmentByRecordId).forEach(([recordId, enrichment]) => {
      if (enrichment.accountManager) {
        map[recordId] = enrichment.accountManager;
      }
    });
    return map;
  }, [missionEnrichmentByRecordId]);

  const refetchAll = useCallback(async () => {
    await Promise.all([kpisQuery.refetch(), coveragesQuery.refetch(), purchaseOrdersQuery.refetch()]);
    setLastUpdatedAt(new Date());
  }, [kpisQuery, coveragesQuery, purchaseOrdersQuery]);

  return {
    loading: kpisQuery.loading || coveragesQuery.loading || purchaseOrdersQuery.loading,
    error: kpisQuery.error ?? coveragesQuery.error ?? purchaseOrdersQuery.error,
    kpis: kpisQuery.data?.purchaseOrderDashboardKpis,
    coverages,
    purchaseOrders: purchaseOrdersQuery.data?.purchaseOrders ?? [],
    missingRows,
    missionEnrichmentByRecordId,
    amByMissionRecordId,
    missingEnrichmentLoading: enrichmentQuery.loading,
    referenceDate: kpisQuery.data?.purchaseOrderDashboardKpis?.referenceDate
      ? new Date(kpisQuery.data.purchaseOrderDashboardKpis.referenceDate)
      : new Date(),
    lastUpdatedAt,
    refetchAll,
  };
};

export default usePurchaseOrderDashboard;
